import { captureSentryException } from '@/integrations/sentry/client';

export interface ConversionInput {
  amount: number;
  fee: number;
  fromCurrency: string;
  toCurrency: string;
}

export interface ConversionResult {
  convertedAmount: number; // rounded to 2 dp
  convertedFee: number;    // rounded to 2 dp
  rate: number;            // fromCurrency → toCurrency
}

/**
 * Converts amount and fee from fromCurrency to toCurrency.
 * Returns the original values unchanged when currencies match.
 * Throws (and logs to Sentry) if rates are unavailable after retries.
 */
export async function convertTransactionAmounts(
  input: ConversionInput,
  fetchRates: (base: string) => Promise<Record<string, number>>
): Promise<ConversionResult> {
  const { amount, fee, fromCurrency, toCurrency } = input;

  // Identity: no conversion needed when currencies match
  if (fromCurrency === toCurrency) {
    return { convertedAmount: amount, convertedFee: fee, rate: 1 };
  }

  try {
    const rates = await fetchRates(fromCurrency);

    if (!(toCurrency in rates)) {
      throw new Error(`Exchange rate not available for ${toCurrency}`);
    }

    const rate = rates[toCurrency];
    const convertedAmount = Math.round(amount * rate * 100) / 100;
    const convertedFee = Math.round(fee * rate * 100) / 100;

    return { convertedAmount, convertedFee, rate };
  } catch (error) {
    captureSentryException(error as Error);
    throw new Error('Currency conversion unavailable: exchange rates could not be fetched');
  }
}
